import React, { useRef, useState } from "react";
import { Input } from "antd";
import style from "css/signin.module.css";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { closeAlert, openAlert } from "../../redux/slices/alertSlice";
import axios from "axios";
import { Cookies } from "react-cookie";
import SignInModal from "component/common/SigninModal";
import { insertUserInfo } from "../../redux/slices/userSlice";

function SignIn(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const isAdmin = props.type === "admin";

  const authNm = process.env.REACT_APP_AUTH_TOKEN || "AUTH_TOKEN"; // 기본값 추가
  const refreshNm = process.env.REACT_APP_REFRESH_TOKEN || "REFRESH_TOKEN";
  const idRef = useRef();
  const passwordRef = useRef();

  const [user, setUser] = useState({
    id: "",
    password: "",
  });
  const [modalInfo, setModalInfo] = useState({
    isOpen: false,
    ticketNumber: null,
    userId: null, // 모달에 전달할 ID
    timestamp: null, // 모달에 전달할 timestamp
  });

  const handleLogin = async () => {
    if (user.id === "") {
      showAlert("학번/교번을 입력해주세요", () => idRef.current.focus());
      return;
    }
    if (user.password === "") {
      showAlert("비밀번호를 입력해주세요", () => passwordRef.current.focus());
      return;
    }

    // 관리자 로그인
    if (isAdmin) {
      const data = {
        ADMIN_ID: encryptAES(user.id),
        ADMIN_PW: encryptAES(user.password),
      };

      axios
        .post("/user/login/admin", data)
        .then((res) => {
          if (res.status === 200) {
            const info = res.data.MEMBER_INFO;
            const expireDate = new Date();
            expireDate.setMinutes(expireDate.getMinutes() + 30);

            cookies.set(authNm, info.AUTH_TOKEN, {
              path: "/",
              expires: expireDate,
            });
            cookies.set(refreshNm, info.REFRESH_TOKEN, {
              path: "/",
              expires: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
            });

            dispatch(
              insertUserInfo({
                userNm: info.MEMBER_NM,
                userId: info.MEMBER_ID,
                email: info.EMAIL,
                adminYn: "Y",
                adminType: info.TYPE || "",
              }),
            );
            navigate(process.env.REACT_APP_CLIENT_URL + "admin/application");
          }
        })
        .catch((err) => {
          const errData = err.response?.data;
          let errMsg = "관리자 로그인에 실패했습니다.";
          if (errData === "CANNOT_FIND_USER")
            errMsg = "등록되지 않은 관리자입니다.";
          else if (errData === "UNAUTHORIZED_ADMIN")
            errMsg = "접근 권한이 없습니다.";
          showAlert(errMsg);
        });
      return;
    }

    const timestamp = Date.now();

    // 사용자 로그인
    const data = {
      id: user.id,
      password: encodeURIComponent(user.password),
      timestamp: timestamp,
    };

    axios
      .post("/user/login", data)
      .then((res) => {
        if (res.status === 200 || res.status === 202) {
          const resData = res.data;

          // 직접 로그인 응답 (LoginResultDTO)
          if (resData && resData.status) {
            if (resData.status === "SUCCESS") {
              handleLoginSuccess(resData);
            } else {
              showAlert(resData.message || "로그인에 실패했습니다.");
            }
            return;
          }

          // 대기열 모드 응답 (ticket number)
          setModalInfo({
            isOpen: true,
            ticketNumber: parseInt(resData),
            userId: user.id,
            timestamp: timestamp,
          });
        }
      })
      .catch((err) => {
        const errMsg =
          err.response?.data?.message || "로그인 요청 중 오류가 발생했습니다.";
        showAlert(errMsg);
      });
  };

  const handleLoginSuccess = (data) => {
    const expireDate = new Date();
    expireDate.setMinutes(expireDate.getMinutes() + 30);

    cookies.set(authNm, data.authToken, {
      path: "/",
      expires: expireDate,
      // secure: true,
    });
    cookies.set(refreshNm, data.refreshToken, {
      path: "/",
      expires: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14일
    });

    console.log(data);

    dispatch(
      insertUserInfo({
        userNm: data.name,
        userId: data.id,
        email: data.email,
        adminYn: "N",
        campus: data.campus || "",
        campusCd: data.campusCd || "",
      }),
    );

    setModalInfo({
      isOpen: false,
      ticketNumber: null,
      userId: null,
      timestamp: null,
    });
    navigate(process.env.REACT_APP_CLIENT_URL + "my");
  };

  // AES 암호화 헬퍼 함수
  const encryptAES = (text) => {
    return CryptoJS.AES.encrypt(
      text,
      CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
      {
        iv: CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY),
        padding: CryptoJS.pad.Pkcs7,
        mode: CryptoJS.mode.CBC,
      },
    ).toString();
  };

  const showAlert = (content, callback) => {
    const obj = {
      title: "알림",
      content: content,
      submitEventHandler: () => {
        if (callback) callback();
        dispatch(closeAlert());
      },
    };
    dispatch(openAlert(obj));
  };

  const handleModalClose = () => {
    setModalInfo({
      isOpen: false,
      ticketNumber: null,
      userId: null,
      timestamp: null,
    });

    setUser((prev) => ({ ...prev, password: "" }));
    if (passwordRef.current) passwordRef.current.focus();
  };

  return (
    <section className={`${style.wrapper}`}>
      {modalInfo.isOpen ? (
        <SignInModal
          isOverlay={false}
          logo={
            <>
              <img src="/icon/logo.png" alt="logo" />
              <p>연세대학교 Adobe 계정 신청 시스템</p>
            </>
          }
          userId={modalInfo.userId}
          ticketNumber={modalInfo.ticketNumber}
          onSuccess={handleLoginSuccess}
          onClose={handleModalClose}
          timestamp={modalInfo.timestamp}
        />
      ) : (
        <section className={`${style.container}`}>
          <section className={`${style.title_section}`}>
            <img src="/icon/logo.png" alt="logo" className={`${style.logo}`} />
            <div className={`${style.title}`}>
              {isAdmin
                ? "연세대학교 Adobe 계정 관리 시스템"
                : "연세대학교 Adobe 계정 신청 시스템"}
            </div>
            <div className={`${style.description}`}>
              {isAdmin
                ? "관리자 계정으로 로그인해 주세요."
                : "연세포털 로그인 정보를 입력해 주세요."}
            </div>
            {!isAdmin && (
              <div className={`${style.guide}`}>
                ※ 로그인 후 신청 페이지에서 라이선스를 신청할 수 있습니다.
                <br />※ 신청 시간 전에 미리 로그인하여 대기해 주세요.
              </div>
            )}
          </section>

          <section className={`${style.form_section}`}>
            <section className={`${style.input_section}`}>
              <Input
                className={`${style.input_item}`}
                ref={idRef}
                placeholder={
                  isAdmin
                    ? "관리자 ID를 입력하세요"
                    : "학번 또는 교번을 입력하세요"
                }
                value={user.id}
                onChange={(e) => setUser({ ...user, id: e.target.value })}
                onPressEnter={handleLogin} // 엔터키 지원 추가
              />
              <Input
                className={`${style.input_item}`}
                type={"password"}
                ref={passwordRef}
                placeholder="비밀번호를 입력하세요"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                onPressEnter={handleLogin}
              />
            </section>

            <button className={`${style.form_btn}`} onClick={handleLogin}>
              <p>{isAdmin ? "관리자 로그인" : "로그인"}</p>
            </button>
          </section>
        </section>
      )}
    </section>
  );
}

export default SignIn;
